import {
    TokenAmount,
    TransactionDetailRow,
    TransactionFeeSection,
    TransactionDialogContent,
    TransactionSection,
} from "@/components";
import { useTokenApprovalV2, useWithdrawTransaction } from "@/hooks";
import { Divider } from "@/components";
import { TOKEN_IDS } from "@/types";
import { useCallback, useMemo } from "react";
import { getGatewayContractAddress } from "@/config";
import { mainnet } from "viem/chains";
import { ActionText } from "@/constants/text.enum";
import { getTransactionLoadingState } from "./transaction-loading.utils";

export interface WithdrawTransactionProps {
    withdrawAmount: string;
    onClose?: () => void;
}

export function WithdrawTransaction({ withdrawAmount, onClose }: WithdrawTransactionProps) {
    const {
        approvalAmount,
        gasPriceUSD,
        executeWithdrawTransaction,
        isLoading: isWithdrawTransactionLoading,
        isExecuting,
    } = useWithdrawTransaction({
        withdrawAmount,
    });

    const { isApprovalLoading, withApproval, approvalLoadingText } = useTokenApprovalV2({
        tokenId: TOKEN_IDS.SIUSD,
        amount: approvalAmount,
        spender: getGatewayContractAddress(mainnet.id),
    });

    const handleConfirm = useCallback(async () => {
        try {
            const hasExecuted = await withApproval(
                async () => {
                    await executeWithdrawTransaction();
                    return true;
                },
                { isExecuting, amount: approvalAmount },
            );

            if (!hasExecuted) return;

            onClose?.();
        } catch (e) {
            console.error("Error executing withdraw transaction:", e);
        }
    }, [withApproval, executeWithdrawTransaction, onClose]);

    const WITHDRAW_TEXT = useMemo(
        () => <TokenAmount amount={withdrawAmount} tokenId={TOKEN_IDS.IUSD} size="md" />,
        [withdrawAmount],
    );
    const CONFIRM_TEXT = "Confirm Withdraw";

    const { isLoading, loadingText } = getTransactionLoadingState({
        approvalLoadingText,
        isApprovalLoading,
        isExecuting,
        actionLoadingText: ActionText.WITHDRAWING,
        isTransactionLoading: isWithdrawTransactionLoading,
    });
    const isDisabled = isWithdrawTransactionLoading || !withdrawAmount;

    return (
        <TransactionDialogContent
            onClose={onClose}
            onConfirm={handleConfirm}
            isLoading={isLoading}
            loadingText={loadingText}
            confirmText={CONFIRM_TEXT}
            disabled={isDisabled}
            className="w-[420px]"
        >
            <TransactionSection>
                <TransactionDetailRow label="Withdraw stake" value={WITHDRAW_TEXT} />
            </TransactionSection>
            <Divider />
            <TransactionFeeSection gasPriceUSD={gasPriceUSD} />
        </TransactionDialogContent>
    );
}
